import { memo, useState, useEffect } from 'react';
import { Box, Flex, useMediaQuery, Button } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { FetchModelBlocks, getBlocks, GetUserModels, resetBlock } from 'redux/ModelsSlice';
import { fetchAllScenarios } from 'redux/ScenarioSlice';
import { handleBuilderModeTab } from 'redux/BuilderModeSlice';
import { useBloxModelSectionTitle } from 'hooks/useBloxDocumentTitle';
import GridMenuIcon from 'components/Icons/GridMenuIcon';
import { UserModalNew } from 'pages/UserManagement/ProfileNewUIRevamp';

import LogoSection from './components/LogoSection';
import NavigationBar from './components/NavigationBar';
import UserActions from './components/UserActions';
import MobileNavigationMenu from './components/MobileNavigationMenu';
import TabContent from './components/TabContent';
import useActiveTab from './hooks/useActiveTab';
import { HEADER_CONFIG, MODEL_TAB_CONFIG } from './constants';

const ModalOverViewPageHeaderV1 = () => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const model = useSelector(getBlocks);
  const { activeTab, setActiveTab } = useActiveTab('dashboard');
  const [isPhone] = useMediaQuery('(max-width: 767px)');
  const [isIpad] = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const sectionLabel =
    MODEL_TAB_CONFIG.find((tab) => tab.tabType === activeTab)?.name ?? 'Dashboard';
  useBloxModelSectionTitle(sectionLabel, model?.name);

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(FetchModelBlocks({ modelId: Number(modelId), loader: true }));
    if (modelId) {
      dispatch(fetchAllScenarios(Number(modelId)));
    }
  }, [modelId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(handleBuilderModeTab(0));
      dispatch(resetBlock());
    };
  }, [dispatch]);

  const showTabletMenu = Boolean(isPhone);
  const headerGridTemplateColumns: string = showTabletMenu || isIpad ? '1fr auto' : '1fr auto 1fr';

  // V1 keeps the original dark body gradient. The Dimensions revamp commit
  // replaced this with the V2 light gradient in place, which restyled the
  // original UI along with the new one — the V2 look belongs to
  // ModalOverViewPageHeaderRevamp, not here.
  return (
    <Box bg='gradient.body' minH='100vh'>
      <Box
        display='flex'
        flexDirection='column'
        w='100%'
        minH={HEADER_CONFIG.HEIGHT}
        px={6}
        pt='10px'
        alignItems='center'
        justifyContent='space-between'
        bg={HEADER_CONFIG.BG_COLOR}
        boxShadow='sm'
        gap='10px'
        borderBottom='1px solid #D9D9D9'
      >
        <Box
          display='grid'
          gridTemplateColumns={headerGridTemplateColumns}
          alignItems='center'
          gap={2}
          width='100%'
        >
          <Box justifySelf='start' minW={0}>
            <LogoSection modelName={model?.name} modelData={model} />
          </Box>
          {!showTabletMenu && !isIpad && (
            <Box display='flex' justifyContent='center'>
              <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />
            </Box>
          )}
          <Flex alignItems='center' gap={2} justifySelf='end'>
            {showTabletMenu && (
              <Button
                variant='solid'
                bg='white'
                borderRadius='full'
                width='40px'
                height='40px'
                minW='40px'
                p={0}
                _hover={{ bg: 'gray.50' }}
                aria-label='Open navigation menu'
                onClick={() => setIsMenuOpen(true)}
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <GridMenuIcon width={20} height={20} color='#6B5FC6' />
              </Button>
            )}
            {!showTabletMenu && <UserActions />}
          </Flex>
        </Box>
        {isIpad && (
          <Box display='flex' alignItems='center' justifyContent='center' px={6} pb={2}>
            <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />
          </Box>
        )}
      </Box>

      <TabContent activeTab={activeTab} blocks={model?.blocks} categories={model?.categories} />

      {showTabletMenu && (
        <MobileNavigationMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      <UserModalNew />
    </Box>
  );
};

export default memo(ModalOverViewPageHeaderV1);
